import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Upsell popup offering a random lash from the Lashes collection when the
 * 18-piece brush collection is added to cart. Excluded from home page.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The popup dialog.
 * @property {HTMLImageElement} lashImage - Product image in the modal.
 * @property {HTMLElement} lashTitle - Product title in the modal.
 * @property {HTMLElement} lashDesc - Product description in the modal.
 * @property {HTMLElement} lashPrice - Product price in the modal.
 * @property {HTMLElement} lashCompare - Strikethrough compare-at price.
 * @property {HTMLButtonElement} addButton - Add to bag button.
 * @property {HTMLElement} addText - Button text label.
 *
 * @extends {Component<Refs>}
 */
class BrushLashUpsell extends Component {
  requiredRefs = ['dialog', 'lashImage', 'lashTitle', 'lashDesc', 'lashPrice', 'lashCompare', 'addButton', 'addText'];
  #openScheduled = false;
  /** @type {object|null} Currently displayed lash data */
  #activeLash = null;

  connectedCallback() {
    super.connectedCallback();
    if (this.#isHomePage()) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.addEventListener('click', this.#handleClick);
    this.refs.dialog.addEventListener('click', this.#handleDialogClick);
    this.#setupFetchInterceptor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.removeEventListener('click', this.#handleClick);
    this.refs.dialog.removeEventListener('click', this.#handleDialogClick);
  }

  #isHomePage() {
    const path = window.location.pathname.replace(/\/+$/, '');
    return path === '' || path === '/' || document.body.classList.contains('template-index');
  }

  /** Return all lash data-entries embedded in the snippet */
  #getLashEntries() {
    return Array.from(this.querySelectorAll('[data-lash-entry]')).map((el) => ({
      handle: el.dataset.lashHandle,
      productId: el.dataset.lashProductId,
      variantId: el.dataset.lashVariantId,
      available: el.dataset.lashAvailable === 'true',
      title: el.dataset.lashTitle,
      desc: el.dataset.lashDesc,
      price: el.dataset.lashPrice,
      compare: el.dataset.lashCompare,
      hasCompare: el.dataset.lashHasCompare === 'true',
      image: el.dataset.lashImage,
      url: el.dataset.lashUrl,
    }));
  }

  /** Pick a random lash (preferring available ones) */
  #pickRandomLash() {
    const entries = this.#getLashEntries();
    if (!entries.length) return null;
    const available = entries.filter((e) => e.available);
    const pool = available.length ? available : entries;
    return pool[Math.floor(Math.random() * pool.length)];
  }

  /** Check if the cart event or fetch data corresponds to the brush product */
  #isBrushProductId(productId) {
    const brushId = String(this.dataset.brushProductId || '');
    return brushId && String(productId) === brushId;
  }

  #isBrushHandle(handle) {
    return (handle || '').toLowerCase() === (this.dataset.brushHandle || '').toLowerCase();
  }

  /** Populate modal refs with a given lash entry */
  #populateModal(lash) {
    this.#activeLash = lash;
    this.refs.lashImage.src = lash.image;
    this.refs.lashImage.alt = lash.title;
    this.refs.lashTitle.textContent = lash.title;
    this.refs.lashDesc.textContent = lash.desc;
    this.refs.lashPrice.textContent = lash.price;

    if (lash.hasCompare) {
      this.refs.lashCompare.textContent = lash.compare;
      this.refs.lashCompare.hidden = false;
    } else {
      this.refs.lashCompare.hidden = true;
    }

    this.refs.addText.textContent = `Add To Bag \u2022 ${lash.price}`;
    this.refs.addButton.disabled = false;
    delete this.refs.addButton.dataset.defaultText;
  }

  #openModal() {
    if (this.refs.dialog.open || this.#openScheduled) return;
    const lash = this.#pickRandomLash();
    if (!lash) return;

    this.#openScheduled = true;
    requestAnimationFrame(() => {
      this.#openScheduled = false;
      if (this.refs.dialog.open || !this.isConnected) return;
      this.#populateModal(lash);
      this.refs.dialog.showModal();
    });
  }

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    if (this.#isHomePage()) return;
    if (event.action !== 'add') return;

    // Check by product ID on the form
    const productForm = event.target instanceof Element ? event.target.closest('[data-product-id]') : null;
    const formProductId = String(productForm?.getAttribute('data-product-id') ?? event.detail?.productId ?? '');
    if (formProductId && !this.#isBrushProductId(formProductId)) return;

    const open = () => this.#openModal();

    if (event.promise && typeof event.promise.then === 'function') {
      event.promise
        .then(({ detail }) => {
          if (!detail?.didError) open();
        })
        .catch(() => open());
    } else {
      open();
    }
  };

  /** Intercept fetch calls to /cart/add to catch brush additions across any page */
  #setupFetchInterceptor() {
    if (window._brushLashInterceptorActive) return;
    window._brushLashInterceptorActive = true;

    const originalFetch = window.fetch;
    const self = this;

    window.fetch = async function (...args) {
      const response = await originalFetch.apply(this, args);
      try {
        if (self.#isHomePage()) return response;
        const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
        if (url.includes('/cart/add') && response.ok) {
          const clone = response.clone();
          clone.json().then((data) => {
            const addedProductId = String(data.product_id || data.id || '');
            const addedHandle = String(data.handle || '');
            if (
              self.#isBrushProductId(addedProductId) ||
              self.#isBrushHandle(addedHandle)
            ) {
              self.#openModal();
            }
          }).catch(() => {});
        }
      } catch (e) {}
      return response;
    };
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-dialog-close]')) {
      this.refs.dialog.close();
      return;
    }

    if (event.target.closest('.blush-upsell__btn-add') || event.target === this.refs.addButton) {
      this.#addLashToBag();
    }
  };

  /** @param {MouseEvent} event */
  #handleDialogClick = (event) => {
    if (event.target === this.refs.dialog) {
      this.refs.dialog.close();
    }
  };

  async #addLashToBag() {
    const lash = this.#activeLash;
    if (!lash || this.refs.addButton.disabled) return;

    this.refs.addButton.disabled = true;
    if (!this.refs.addButton.dataset.defaultText) {
      this.refs.addButton.dataset.defaultText = this.refs.addText.textContent;
    }
    this.refs.addText.textContent = 'Adding to bag...';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ id: lash.variantId, quantity: 1 }),
      });

      if (response.ok) {
        this.refs.addText.textContent = 'Added to Bag \u2713';

        // Refresh cart count
        try {
          const cartRes = await fetch('/cart.js');
          const cartData = await cartRes.json();
          document.querySelectorAll('.cart-count, [data-cart-count]').forEach((el) => {
            el.textContent = cartData.item_count;
          });
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            setTimeout(() => {
              this.refs.dialog.close();
              cartDrawer.open();
            }, 600);
            return;
          }
        } catch (e) {}

        setTimeout(() => {
          this.refs.dialog.close();
        }, 1000);
      } else {
        this.refs.addText.textContent = 'Could not add to bag';
        setTimeout(() => this.#resetButtonState(), 1800);
      }
    } catch (err) {
      this.refs.addText.textContent = 'Error adding item';
      setTimeout(() => this.#resetButtonState(), 1800);
    }
  }

  #resetButtonState() {
    this.refs.addButton.disabled = false;
    const defaultText = this.refs.addButton.dataset.defaultText;
    if (defaultText) this.refs.addText.textContent = defaultText;
    else if (this.#activeLash) this.refs.addText.textContent = `Add To Bag \u2022 ${this.#activeLash.price}`;
  }
}

if (!customElements.get('brush-lash-upsell')) {
  customElements.define('brush-lash-upsell', BrushLashUpsell);
}
